export const READING_ANNOTATION_VERSION = 1;

const USER_HIGHLIGHT_CLASS = 'ielts-reading-note-highlight';
const OFFICIAL_HIGHLIGHT_CLASS = 'ielts-official-answer-highlight';
const GENERATED_TEXT_SELECTOR = '.ielts-official-answer-label, .ielts-official-answer-info';
const CONTEXT_LENGTH = 32;

function collectTextNodes(root) {
  const nodes = [];
  if (!root) return nodes;

  const ownerDocument = root.ownerDocument || document;
  const walker = ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.parentElement?.closest(GENERATED_TEXT_SELECTOR)) return NodeFilter.FILTER_REJECT;
      if (node.parentElement?.closest('script, style, noscript')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

function parsePassage(html) {
  const template = document.createElement('template');
  template.innerHTML = String(html || '');
  return template;
}

function getTextFromRoot(root) {
  return collectTextNodes(root).map((node) => node.nodeValue || '').join('');
}

function isValidRange(annotation, textLength) {
  return annotation
    && Number.isInteger(Number(annotation.start))
    && Number.isInteger(Number(annotation.end))
    && Number(annotation.start) >= 0
    && Number(annotation.end) > Number(annotation.start)
    && Number(annotation.end) <= textLength;
}

function withCurrentContext(annotation, text) {
  const start = Number(annotation.start);
  const end = Number(annotation.end);
  return {
    ...annotation,
    start,
    end,
    text: text.slice(start, end),
    prefix: text.slice(Math.max(0, start - CONTEXT_LENGTH), start),
    suffix: text.slice(end, end + CONTEXT_LENGTH),
    note: String(annotation.note || ''),
    type: annotation.type === 'note' ? 'note' : 'marker',
  };
}

function rangesOverlap(first, second) {
  return Number(first.start) < Number(second.end) && Number(first.end) > Number(second.start);
}

function plainText(html) {
  const template = parsePassage(html);
  return template.content.textContent?.trim() || '';
}

function normalizeOfficialHighlights(input, text) {
  let highlights = input;
  if (typeof highlights === 'string') {
    try {
      highlights = JSON.parse(highlights);
    } catch (_) {
      highlights = [];
    }
  }
  if (!Array.isArray(highlights)) return [];

  return highlights.map((item, index) => {
    let start = Math.floor(Number(item?.start));
    let end = Math.floor(Number(item?.end));
    const selectedText = String(item?.selectedText || item?.selected_text || item?.text || '');
    if (selectedText && text.slice(start, end) !== selectedText) {
      const candidates = [];
      let matchIndex = text.indexOf(selectedText);
      while (matchIndex >= 0) {
        candidates.push(matchIndex);
        matchIndex = text.indexOf(selectedText, matchIndex + 1);
      }
      if (candidates.length) {
        candidates.sort((first, second) => Math.abs(first - start) - Math.abs(second - start));
        start = candidates[0];
        end = start + selectedText.length;
      }
    }
    if (!isValidRange({ start, end }, text.length)) return null;

    const rawRange = String(item.questionRange || item.question_range || item.question || item.label || '').trim();
    const rangeMatch = rawRange.replace(/^questions?\s*/i, '').replace(/^q\s*/i, '').replace(/\s+/g, '').match(/^(\d+)(?:-(\d+))?$/);
    const firstQuestion = rangeMatch ? Number(rangeMatch[1]) : null;
    const lastQuestion = rangeMatch ? Number(rangeMatch[2] || rangeMatch[1]) : null;
    const questionNumbers = [];
    if (firstQuestion && lastQuestion >= firstQuestion) {
      for (let number = firstQuestion; number <= lastQuestion; number += 1) questionNumbers.push(String(number));
    }

    return {
      id: String(item.id || `official-${start}-${end}-${index}`),
      start,
      end,
      questionRange: questionNumbers.length ? (firstQuestion === lastQuestion ? String(firstQuestion) : `${firstQuestion}-${lastQuestion}`) : '',
      questionNumbers,
      explanation: String(item.explanation || item.explanationHtml || item.explanation_html || item.note || ''),
    };
  }).filter(Boolean);
}

function groupOfficialHighlights(highlights) {
  let groups = [];

  highlights.forEach((highlight) => {
    const overlappingGroups = groups.filter((group) => (
      group.some((item) => rangesOverlap(item, highlight))
    ));

    if (overlappingGroups.length === 0) {
      groups.push([highlight]);
      return;
    }

    groups = groups.filter((group) => !overlappingGroups.includes(group));
    groups.push([highlight, ...overlappingGroups.flat()]);
  });

  return groups.map((group) => {
    if (group.length === 1) return group[0];

    const orderedGroup = [...group].sort((first, second) => (
      Number(first.questionNumbers[0] || Number.MAX_SAFE_INTEGER)
      - Number(second.questionNumbers[0] || Number.MAX_SAFE_INTEGER)
    ));
    const questionRanges = [...new Set(orderedGroup.map((item) => item.questionRange).filter(Boolean))];
    const questionNumbers = [...new Set(orderedGroup.flatMap((item) => item.questionNumbers))];
    const explanation = orderedGroup.map((item) => (
      `<p><strong>Q${item.questionRange || 'answer'}</strong></p>${item.explanation || '<p>No explanation available.</p>'}`
    )).join('');

    return {
      ...orderedGroup[0],
      id: orderedGroup.map((item) => item.id).join(','),
      start: Math.min(...orderedGroup.map((item) => item.start)),
      end: Math.max(...orderedGroup.map((item) => item.end)),
      questionRange: questionRanges.join(', '),
      questionRanges,
      questionNumbers,
      explanation,
      questionLabel: questionRanges.map((range) => `Q${range}`).join(', '),
    };
  }).sort((first, second) => first.start - second.start || first.end - second.end);
}

export function getCanonicalReadingText(html) {
  return getTextFromRoot(parsePassage(html).content);
}

export function composeReadingPassageHtml(html, userAnnotations = [], officialInput = []) {
  const template = parsePassage(html);
  const text = getTextFromRoot(template.content);
  const users = (Array.isArray(userAnnotations) ? userAnnotations : [])
    .filter((annotation) => isValidRange(annotation, text.length));
  const officials = groupOfficialHighlights(normalizeOfficialHighlights(officialInput, text));
  const nodes = collectTextNodes(template.content);
  let nodeStart = 0;

  nodes.forEach((node) => {
    const value = node.nodeValue || '';
    const nodeEnd = nodeStart + value.length;
    const touchingUsers = users.filter((item) => item.start < nodeEnd && item.end > nodeStart);
    const touchingOfficials = officials.filter((item) => item.start < nodeEnd && item.end > nodeStart);

    if (touchingUsers.length || touchingOfficials.length) {
      const boundaries = new Set([0, value.length]);
      [...touchingUsers, ...touchingOfficials].forEach((item) => {
        boundaries.add(Math.max(0, Number(item.start) - nodeStart));
        boundaries.add(Math.min(value.length, Number(item.end) - nodeStart));
      });

      const sorted = [...boundaries].sort((first, second) => first - second);
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < sorted.length - 1; index += 1) {
        const localStart = sorted[index];
        const localEnd = sorted[index + 1];
        if (localEnd <= localStart) continue;

        const segmentText = value.slice(localStart, localEnd);
        const globalStart = nodeStart + localStart;
        const globalEnd = nodeStart + localEnd;
        const user = touchingUsers.find((item) => item.start < globalEnd && item.end > globalStart);
        const official = touchingOfficials.find((item) => item.start < globalEnd && item.end > globalStart);

        if (!user && !official) {
          fragment.appendChild(document.createTextNode(segmentText));
          continue;
        }

        const mark = document.createElement('mark');
        mark.textContent = segmentText;

        if (user) {
          mark.classList.add(USER_HIGHLIGHT_CLASS);
          mark.dataset.readingHighlightId = String(user.id);
          mark.dataset.highlightType = user.type === 'note' ? 'note' : 'marker';
          mark.title = user.note ? `Note: ${user.note}` : 'Click for options';
          mark.setAttribute('role', 'button');
          mark.setAttribute('tabindex', '0');
        }

        if (official) {
          mark.classList.add(OFFICIAL_HIGHLIGHT_CLASS);
          mark.dataset.officialHighlightId = official.id;
          mark.dataset.questionRange = official.questionRange;
          mark.dataset.questionNumbers = official.questionNumbers.join(',');
          mark.dataset.explanation = official.explanation;
          mark.title = official.explanation ? plainText(official.explanation) : `Answer location for Q${official.questionRange}`;
          if (user) mark.classList.add('ielts-official-answer-highlight-user-overlap');

          if (globalEnd === official.end && official.questionRange) {
            const label = document.createElement('span');
            label.className = 'ielts-official-answer-label';
            label.textContent = official.questionLabel || `Q${official.questionRange}`;
            label.setAttribute('aria-hidden', 'true');

            const info = document.createElement('span');
            info.className = 'ielts-official-answer-info';
            info.textContent = 'i';
            info.dataset.officialInfo = 'true';
            info.setAttribute('role', 'button');
            info.setAttribute('tabindex', '0');
            mark.append(label, info);
          }
        }

        fragment.appendChild(mark);
      }

      node.replaceWith(fragment);
    }

    nodeStart = nodeEnd;
  });

  return template.innerHTML;
}

export function getReadingSelectionOffsets(container, range) {
  if (!container || !range || !container.contains(range.commonAncestorContainer)) return null;
  const nodes = collectTextNodes(container);
  let offset = 0;
  let start = null;
  let end = null;

  nodes.forEach((node) => {
    if (node === range.startContainer) start = offset + range.startOffset;
    if (node === range.endContainer) end = offset + range.endOffset;
    offset += (node.nodeValue || '').length;
  });

  if (start === null || end === null || end <= start) return null;
  const text = nodes.map((node) => node.nodeValue || '').join('');
  return { start, end, text: text.slice(start, end) };
}

export function mergeReadingAnnotation(html, existingAnnotations, newAnnotation) {
  const text = getCanonicalReadingText(html);
  if (!isValidRange(newAnnotation, text.length)) return Array.isArray(existingAnnotations) ? existingAnnotations : [];

  const existing = (Array.isArray(existingAnnotations) ? existingAnnotations : [])
    .filter((annotation) => isValidRange(annotation, text.length));
  const overlapping = existing.filter((annotation) => rangesOverlap(annotation, newAnnotation));
  if (!overlapping.length) {
    return [...existing, withCurrentContext(newAnnotation, text)].sort((first, second) => first.start - second.start);
  }

  const untouched = existing.filter((annotation) => !rangesOverlap(annotation, newAnnotation));
  const noted = overlapping.find((annotation) => annotation.type === 'note' && annotation.note);
  const merged = withCurrentContext({
    ...newAnnotation,
    id: noted?.id || newAnnotation.id,
    type: noted ? 'note' : newAnnotation.type,
    note: noted?.note || newAnnotation.note || '',
    start: Math.min(Number(newAnnotation.start), ...overlapping.map((annotation) => Number(annotation.start))),
    end: Math.max(Number(newAnnotation.end), ...overlapping.map((annotation) => Number(annotation.end))),
  }, text);

  return [...untouched, merged].sort((first, second) => first.start - second.start);
}

export function restoreReadingAnnotations(html, storedAnnotations) {
  const text = getCanonicalReadingText(html);
  if (!Array.isArray(storedAnnotations)) return [];

  return storedAnnotations.map((stored) => {
    const selectedText = String(stored?.text || '');
    if (!selectedText) return null;

    const directStart = Number(stored.start);
    const directEnd = Number(stored.end);
    if (isValidRange({ start: directStart, end: directEnd }, text.length)
      && text.slice(directStart, directEnd) === selectedText) {
      return withCurrentContext({ ...stored, start: directStart, end: directEnd }, text);
    }

    const candidates = [];
    let index = text.indexOf(selectedText);
    while (index >= 0) {
      const prefix = String(stored.prefix || '');
      const suffix = String(stored.suffix || '');
      let score = 0;
      if (prefix && text.slice(Math.max(0, index - prefix.length), index) === prefix) score += 2;
      if (suffix && text.slice(index + selectedText.length, index + selectedText.length + suffix.length) === suffix) score += 2;
      score -= Math.abs(index - (Number.isFinite(directStart) ? directStart : index)) / Math.max(text.length, 1);
      candidates.push({ start: index, score });
      index = text.indexOf(selectedText, index + 1);
    }

    if (!candidates.length) return null;
    candidates.sort((first, second) => second.score - first.score);
    if (candidates.length > 1 && candidates[0].score === candidates[1].score) return null;
    return withCurrentContext({
      ...stored,
      start: candidates[0].start,
      end: candidates[0].start + selectedText.length,
    }, text);
  }).filter(Boolean);
}
